import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    ChevronDown,
    ChevronUp,
    Info,
    Loader2,
    MoreVertical,
    Trash2,
} from "lucide-react";

const PROVIDER_LABELS = {
    google: "Google",
    smtp: "SMTP",
};

const formatProvider = (provider) => {
    const normalizedProvider = String(provider || "").trim().toLowerCase();

    if (!normalizedProvider) return "Unknown provider";
    if (PROVIDER_LABELS[normalizedProvider]) {
        return PROVIDER_LABELS[normalizedProvider];
    }

    return normalizedProvider
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, (character) => character.toUpperCase());
};

const getAccountInitial = (connection) => {
    const value =
        connection?.account_identifier || connection?.provider || "sender";

    return String(value).trim().charAt(0).toUpperCase() || "S";
};

const getTestStatusLabel = (testStatus, testResult) => {
    if (testStatus === "loading") return "Testing...";
    if (testStatus === "failed") return "Failed";

    const resultStatus = String(testResult?.status || "")
        .trim()
        .toLowerCase();

    if (resultStatus === "healthy") return "Healthy";
    if (resultStatus === "unhealthy") return "Unhealthy";

    return "Not tested";
};

const getTestStatusClassName = (testStatus, testResult) => {
    const resultStatus = String(testResult?.status || "")
        .trim()
        .toLowerCase();

    if (testStatus === "loading") {
        return "text-indigo-700";
    }

    if (testStatus === "failed" || resultStatus === "unhealthy") {
        return "text-red-700";
    }

    if (resultStatus === "healthy") {
        return "text-green-700";
    }

    return "text-slate-600";
};

const PRIORITY_OPTIONS = [
    { value: 10, label: "Primary" },
    { value: 50, label: "High" },
    { value: 100, label: "Standard" },
    { value: 200, label: "Low" },
    { value: 500, label: "Least" },
];

const TOOLTIP_WIDTH = 288;
const TOOLTIP_EDGE_GAP = 16;
const ACTION_MENU_WIDTH = 208;
const ACTION_MENU_EDGE_GAP = 16;

const ConnectionAccountCard = ({
    connection = {},
    onTestConnection,
    onSetPrimaryConnection,
    onPriorityChange,
    onDeleteConnection,
    isSettingPrimary = false,
    isDeleting = false,
    priorityStatus = "idle",
    testStatus = "idle",
    testResult = null,
    testError = null,
}) => {
    const actionMenuButtonRef = useRef(null);
    const actionMenuRef = useRef(null);
    const priorityButtonRef = useRef(null);
    const priorityTooltipRef = useRef(null);
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
    const [actionMenuPosition, setActionMenuPosition] = useState({
        top: 0,
        left: 0,
    });
    const [isPriorityTooltipOpen, setIsPriorityTooltipOpen] = useState(false);
    const [isPrioritySelectOpen, setIsPrioritySelectOpen] = useState(false);
    const [priorityTooltipPosition, setPriorityTooltipPosition] = useState({
        top: 0,
        left: 0,
    });
    const providerLabel = formatProvider(connection.provider);
    const isActive = connection.is_active === true;
    const isPrimary = connection.is_primary === true;
    const isTesting = testStatus === "loading";
    const isUpdatingPriority = priorityStatus === "loading";
    const canSetPrimary = isActive && !isPrimary;
    const accountIdentifier =
        connection.account_identifier || "Unknown sender account";
    const numericPriority = Number(connection.priority);
    const hasNumericPriority = Number.isFinite(numericPriority);
    const priorityOption = PRIORITY_OPTIONS.find(
        (option) => option.value === numericPriority,
    );
    const prioritySelectValue = priorityOption
        ? String(priorityOption.value)
        : hasNumericPriority
          ? `custom:${numericPriority}`
          : "";
    const testLabel = getTestStatusLabel(testStatus, testResult);
    const updateActionMenuPosition = useCallback(() => {
        const button = actionMenuButtonRef.current;

        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const menuHeight =
            actionMenuRef.current?.offsetHeight || (canSetPrimary ? 136 : 96);
        const menuWidth = Math.min(
            ACTION_MENU_WIDTH,
            Math.max(0, window.innerWidth - ACTION_MENU_EDGE_GAP * 2),
        );
        const maxLeft = Math.max(
            ACTION_MENU_EDGE_GAP,
            window.innerWidth - menuWidth - ACTION_MENU_EDGE_GAP,
        );
        const hasRoomBelow =
            buttonRect.bottom + 8 + menuHeight <=
            window.innerHeight - ACTION_MENU_EDGE_GAP;
        const top = hasRoomBelow
            ? buttonRect.bottom + 8
            : Math.max(
                  ACTION_MENU_EDGE_GAP,
                  buttonRect.top - menuHeight - 8,
              );
        const left = Math.min(
            Math.max(ACTION_MENU_EDGE_GAP, buttonRect.right - menuWidth),
            maxLeft,
        );

        setActionMenuPosition({ top, left });
    }, [canSetPrimary]);
    const closeActionMenu = useCallback(() => {
        setIsActionMenuOpen(false);
    }, []);
    const toggleActionMenu = () => {
        if (isActionMenuOpen) {
            closeActionMenu();
            return;
        }

        updateActionMenuPosition();
        setIsActionMenuOpen(true);
    };
    const updatePriorityTooltipPosition = useCallback(() => {
        const button = priorityButtonRef.current;

        if (!button) return;

        const buttonRect = button.getBoundingClientRect();
        const tooltipHeight = priorityTooltipRef.current?.offsetHeight || 188;
        const tooltipWidth = Math.min(
            TOOLTIP_WIDTH,
            Math.max(0, window.innerWidth - TOOLTIP_EDGE_GAP * 2),
        );
        const maxLeft = Math.max(
            TOOLTIP_EDGE_GAP,
            window.innerWidth - tooltipWidth - TOOLTIP_EDGE_GAP,
        );
        const hasRoomBelow =
            buttonRect.bottom + 8 + tooltipHeight <=
            window.innerHeight - TOOLTIP_EDGE_GAP;
        const top = hasRoomBelow
            ? buttonRect.bottom + 8
            : Math.max(
                  TOOLTIP_EDGE_GAP,
                  buttonRect.top - tooltipHeight - 8,
              );
        const left = Math.min(
            Math.max(TOOLTIP_EDGE_GAP, buttonRect.right - tooltipWidth),
            maxLeft,
        );

        setPriorityTooltipPosition({ top, left });
    }, []);
    const showPriorityTooltip = useCallback(() => {
        updatePriorityTooltipPosition();
        setIsPriorityTooltipOpen(true);
    }, [updatePriorityTooltipPosition]);
    const hidePriorityTooltip = useCallback(() => {
        setIsPriorityTooltipOpen(false);
    }, []);
    const handlePriorityChange = (event) => {
        const selectedPriority = Number(event.target.value);
        setIsPrioritySelectOpen(false);

        if (!Number.isFinite(selectedPriority)) return;
        if (selectedPriority === numericPriority) return;

        onPriorityChange?.(selectedPriority);
    };
    const handleTestConnection = () => {
        closeActionMenu();
        onTestConnection?.();
    };
    const handleSetPrimary = () => {
        closeActionMenu();
        onSetPrimaryConnection?.();
    };
    const handleDeleteConnection = () => {
        closeActionMenu();
        onDeleteConnection?.();
    };

    useEffect(() => {
        if (!isPriorityTooltipOpen) return undefined;

        updatePriorityTooltipPosition();
        window.addEventListener("resize", updatePriorityTooltipPosition);
        window.addEventListener("scroll", updatePriorityTooltipPosition, true);

        return () => {
            window.removeEventListener("resize", updatePriorityTooltipPosition);
            window.removeEventListener(
                "scroll",
                updatePriorityTooltipPosition,
                true,
            );
        };
    }, [isPriorityTooltipOpen, updatePriorityTooltipPosition]);

    useEffect(() => {
        if (!isActionMenuOpen) return undefined;

        updateActionMenuPosition();

        const handlePointerDown = (event) => {
            if (
                actionMenuRef.current?.contains(event.target) ||
                actionMenuButtonRef.current?.contains(event.target)
            ) {
                return;
            }

            closeActionMenu();
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeActionMenu();
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", closeActionMenu);
        window.addEventListener("scroll", closeActionMenu, true);

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", closeActionMenu);
            window.removeEventListener("scroll", closeActionMenu, true);
        };
    }, [closeActionMenu, isActionMenuOpen, updateActionMenuPosition]);

    return (
        <>
            <article
                className={`relative border-b border-l-4 border-slate-200 px-4 py-3 transition-colors ${
                    isPrimary
                        ? "border-l-[#f97316] bg-[#fff7ed] shadow-[inset_0_0_0_1px_rgba(249,115,22,0.16)]"
                        : "border-l-transparent odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                }`}
            >
                <div className="flex flex-col gap-4 pr-12 lg:flex-row lg:items-center lg:pr-0">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-base font-bold text-indigo-700 ring-1 ring-indigo-100">
                            {getAccountInitial(connection)}
                        </div>

                        <div className="min-w-0">
                            <p
                                className="truncate text-sm font-semibold text-slate-900"
                                title={accountIdentifier}
                            >
                                {accountIdentifier}
                            </p>
                            <div className="mt-1.5 text-xs">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1 font-semibold text-slate-700">
                                        {providerLabel}
                                    </span>
                                    {isPrimary && (
                                        <span className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                            <span
                                                className="h-2 w-2 rounded-full bg-indigo-500"
                                                aria-hidden="true"
                                            />
                                            Primary Sender
                                        </span>
                                    )}
                                    <span
                                        className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                                            isActive
                                                ? "bg-green-50 text-green-700"
                                                : "bg-slate-100 text-slate-600"
                                        }`}
                                    >
                                        <span
                                            className={`h-2 w-2 rounded-full ${
                                                isActive
                                                    ? "bg-green-500"
                                                    : "bg-slate-400"
                                            }`}
                                        />
                                        {isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5">
                                    <span className="font-medium text-slate-400">
                                        Connection status:
                                    </span>
                                    <span
                                        className={`inline-flex items-center gap-1.5 font-semibold ${getTestStatusClassName(
                                            testStatus,
                                            testResult,
                                        )}`}
                                    >
                                        {isTesting && (
                                            <Loader2
                                                size={13}
                                                className="animate-spin"
                                                aria-hidden="true"
                                            />
                                        )}
                                        {testLabel}
                                    </span>
                                    {testStatus === "failed" && testError && (
                                        <span
                                            className="truncate text-red-600"
                                            title={testError}
                                        >
                                            {testError}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-wrap items-center gap-4 lg:ml-auto lg:justify-end">
                        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                            <span className="flex items-center gap-1 text-xs font-medium text-slate-500">
                                <button
                                    ref={priorityButtonRef}
                                    type="button"
                                    onMouseEnter={showPriorityTooltip}
                                    onMouseLeave={hidePriorityTooltip}
                                    onFocus={showPriorityTooltip}
                                    onBlur={hidePriorityTooltip}
                                    onKeyDown={(event) => {
                                        if (event.key === "Escape") {
                                            hidePriorityTooltip();
                                        }
                                    }}
                                    className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                                    aria-label="Priority information"
                                >
                                    <Info size={12} aria-hidden="true" />
                                </button>
                                Priority:
                                {isPriorityTooltipOpen && (
                                    <span
                                        ref={priorityTooltipRef}
                                        role="tooltip"
                                        style={{
                                            top: `${priorityTooltipPosition.top}px`,
                                            left: `${priorityTooltipPosition.left}px`,
                                        }}
                                        className="pointer-events-none fixed z-[120] w-72 max-w-[calc(100vw-2rem)] whitespace-normal rounded-xl border border-slate-200 bg-white px-3 py-3 text-left text-xs font-medium leading-5 text-slate-600 shadow-xl"
                                    >
                                        <span className="block font-semibold text-slate-900">
                                            Lower numbers are used first.
                                        </span>
                                        <span className="mt-2 block">
                                            10 = Primary
                                        </span>
                                        <span className="block">50 = High</span>
                                        <span className="block">
                                            100 = Standard
                                        </span>
                                        <span className="block">200 = Low</span>
                                        <span className="block">
                                            500 = Least
                                        </span>
                                        <span className="mt-2 block">
                                            If the primary sender fails, the
                                            next available connection is used.
                                        </span>
                                    </span>
                                )}
                            </span>
                            <div className="relative min-w-[132px]">
                                <select
                                    value={prioritySelectValue}
                                    onChange={handlePriorityChange}
                                    onClick={() =>
                                        setIsPrioritySelectOpen(
                                            (isOpen) => !isOpen,
                                        )
                                    }
                                    onBlur={() =>
                                        setIsPrioritySelectOpen(false)
                                    }
                                    onKeyDown={(event) => {
                                        if (
                                            [
                                                "Enter",
                                                " ",
                                                "ArrowDown",
                                                "ArrowUp",
                                            ].includes(event.key)
                                        ) {
                                            setIsPrioritySelectOpen(true);
                                        }
                                        if (event.key === "Escape") {
                                            setIsPrioritySelectOpen(false);
                                        }
                                    }}
                                    disabled={
                                        isUpdatingPriority || !onPriorityChange
                                    }
                                    aria-label="Update connection priority"
                                    className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-2.5 py-2 pr-7 text-xs font-semibold text-slate-900 shadow-sm transition hover:border-indigo-200 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                                >
                                    {!priorityOption && hasNumericPriority && (
                                        <option
                                            value={`custom:${numericPriority}`}
                                        >
                                            {numericPriority} - Custom
                                        </option>
                                    )}
                                    {!priorityOption && !hasNumericPriority && (
                                        <option value="">Not set</option>
                                    )}
                                    {PRIORITY_OPTIONS.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.value} - {option.label}
                                        </option>
                                    ))}
                                </select>
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                                    {isUpdatingPriority ? "..." : isPrioritySelectOpen ? (
                                        <ChevronUp
                                            size={14}
                                            aria-hidden="true"
                                        />
                                    ) : (
                                        <ChevronDown
                                            size={14}
                                            aria-hidden="true"
                                        />
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button
                        ref={actionMenuButtonRef}
                        type="button"
                        onClick={toggleActionMenu}
                        className="absolute right-4 top-3 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200 lg:static"
                        aria-label="Connection actions"
                        aria-haspopup="menu"
                        aria-expanded={isActionMenuOpen}
                    >
                        <MoreVertical size={17} aria-hidden="true" />
                    </button>
                </div>
            </article>

            {isActionMenuOpen &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={actionMenuRef}
                        role="menu"
                        style={{
                            top: `${actionMenuPosition.top}px`,
                            left: `${actionMenuPosition.left}px`,
                            width: `${ACTION_MENU_WIDTH}px`,
                        }}
                        className="fixed z-[140] rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
                    >
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleTestConnection}
                            disabled={isTesting || !onTestConnection}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                            {isTesting && (
                                <Loader2
                                    size={15}
                                    className="animate-spin"
                                    aria-hidden="true"
                                />
                            )}
                            {isTesting ? "Testing..." : "Test Connection"}
                        </button>
                        {canSetPrimary && (
                            <button
                                type="button"
                                role="menuitem"
                                onClick={handleSetPrimary}
                                disabled={
                                    isSettingPrimary ||
                                    !onSetPrimaryConnection
                                }
                                className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-indigo-50 hover:text-indigo-700 disabled:cursor-not-allowed disabled:text-slate-400"
                            >
                                {isSettingPrimary
                                    ? "Setting Primary..."
                                    : "Set as Primary Sender"}
                            </button>
                        )}
                        <button
                            type="button"
                            role="menuitem"
                            title="Delete connection"
                            onClick={handleDeleteConnection}
                            disabled={isDeleting || !onDeleteConnection}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:text-slate-400"
                        >
                            <Trash2 size={15} aria-hidden="true" />
                            {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default ConnectionAccountCard;
